import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, ChevronRight } from 'lucide-react';

interface Announcement {
  id: string;
  text: string;
  link?: string;
}

interface AnnouncementTickerProps {
  announcements: Announcement[];
}

export function AnnouncementTicker({ announcements }: AnnouncementTickerProps) {
  const [isPaused, setIsPaused] = useState(false);
  const tickerRef = useRef<HTMLDivElement>(null);

  if (!announcements || announcements.length === 0) return null;

  // Duplicate announcements for seamless loop
  const displayAnnouncements = [...announcements, ...announcements];

  return (
    <div 
      className="bg-primary/10 border-b border-primary/20 py-1 md:py-2 overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="container flex items-center gap-2 md:gap-3">
        <div className="flex items-center gap-1 md:gap-2 shrink-0">
          <Megaphone className="h-3 w-3 md:h-4 md:w-4 text-primary" />
          <span className="text-[10px] md:text-xs font-semibold text-primary uppercase tracking-wider hidden sm:inline">News</span>
        </div>
        
        <div className="overflow-hidden flex-1 relative">
          <div 
            ref={tickerRef}
            className={`flex gap-12 whitespace-nowrap ${isPaused ? '' : 'animate-ticker'}`}
            style={{ 
              animationPlayState: isPaused ? 'paused' : 'running',
            }}
          >
            {displayAnnouncements.map((announcement, index) => (
              <div key={`${announcement.id}-${index}`} className="flex items-center gap-2">
                {announcement.link ? (
                  <Link 
                    to={announcement.link}
                    className="text-xs md:text-sm text-foreground/90 hover:text-primary transition-colors flex items-center gap-1"
                  >
                    {announcement.text}
                    <ChevronRight className="h-2.5 w-2.5 md:h-3 md:w-3" />
                  </Link>
                ) : (
                  <span className="text-xs md:text-sm text-foreground/90">{announcement.text}</span>
                )}
                <span className="text-primary/50">•</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes ticker {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .animate-ticker {
          animation: ticker 30s linear infinite;
          will-change: transform;
        }
        @media (max-width: 768px) {
          .animate-ticker {
            animation-duration: 20s;
          }
        }
      `}</style>
    </div>
  );
}
